import { Component, OnInit, OnDestroy,Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faIcons } from '../../../shared/icons/fa-icons';
import { AuthService } from '../../auth/auth.service';
import { AlertService } from '../../../shared/alert/service/alert.service';
import { Subscription } from 'rxjs';
import { ImpressionTrackingService } from '../../../core/services/impression-tracking.service';
import { RoleSwitcherComponent } from '../../../shared/components/roleSwitcher/role-switcher.component';

export interface MenuItem {
  label: string;
  icon?: IconDefinition;
  route?: string;
  isLeafNode: boolean;
  children?: MenuItem[];
  expanded?: boolean;
  pageName?: string;
  role?: 'admin' | 'vendor' | 'customer';
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterLink, FontAwesomeModule, RoleSwitcherComponent],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.css'
})
export class SidebarComponent implements OnInit, OnDestroy {

  @Input() isMobileOpen = false;  

  faIcons = faIcons;
  filteredMenuItems: MenuItem[] = [];
  private authSub!: Subscription;
  isSidebarOpen = false;

  // ─── Multi-role support ─────────────────────────────────────────────────
  // Global control — this is the ONE place the role switcher lives, since
  // the sidebar is present on every authenticated page.
  canSwitchRole = false;

  menuItems: MenuItem[] = [
    // ─── CUSTOMER ──────────────────────────────────────────────────────────
    {
      label: 'Customer',
      role: 'customer',
      icon: faIcons.user,
      route: '/customer/dashboard',
      isLeafNode: false,
      expanded: false,
      children: [
        {
          label: 'Dashboard',
          pageName: 'Dashboard',
          icon: faIcons.dashboard,
          route: '/customer/dashboard',
          isLeafNode: true
        },
        {
          label: 'Notifications',
          icon: faIcons.bell,
          route: '/customer/notifications',
          isLeafNode: true
        },
        {
          label: 'My Purchases',
          pageName: 'My Purchases',
          icon: faIcons.shoppingBag,
          route: '/customer/mypurchase',
          isLeafNode: true
        },
        {
          label: 'Reviews',
          pageName: 'Reviews',
          icon: faIcons.comments,
          route: '/customer/review',
          isLeafNode: true
        },
        {
          label: 'Offers & Ads',
          pageName: 'Offers & Ads',
          icon: faIcons.gift,
          route: '/customer/offers&ads',
          isLeafNode: true
        },
        {
          label: 'Requests',
          pageName: 'Requests',
          icon: faIcons.clipboard,
          route: '/customer/request',
          isLeafNode: true
        },
        {
          label: 'Profile',
          pageName: 'Profile',
          icon: faIcons.userCircle,
          route: '/customer/profile',
          isLeafNode: true
        }
      ]
    },

    // ─── ADMIN ─────────────────────────────────────────────────────────────
    {
      label: 'Admin',
      role: 'admin',
      icon: faIcons.user,
      route: '/admin',
      isLeafNode: false,
      expanded: false,
      children: [
        {
          label: 'Dashboard',
          pageName: 'Dashboard',
          icon: faIcons.dashboard,
          route: '/admin/dashboard',
          isLeafNode: true
        },
        {
          label: 'Categories',
          pageName: 'Category',
          icon: faIcons.tags,
          route: '/admin/category',
          isLeafNode: true
        },
        {
          label: 'Vendor Categories',
          pageName: 'Vendor Category Admin',
          icon: faIcons.tags,
          route: '/admin/vendor/category',
          isLeafNode: true
        },
        {
          label: 'Vendors',
          pageName: 'All Vendors',
          icon: faIcons.box,
          route: '/admin/vendor',
          isLeafNode: true
        },
        {
          label: 'Review Moderation',
          pageName: 'Review Moderation',
          icon: faIcons.star,
          route: '/admin/review-moderation',
          isLeafNode: true
        },
        {
          label: 'Requests',
          pageName: 'Requests',
          icon: faIcons.clipboard,
          route: '/admin/request',
          isLeafNode: true
        },
        {
          label: 'Users',
          pageName: 'User Management',
          icon: faIcons.user,
          route: '/admin/user-management',
          isLeafNode: true
        },
        {
          label: 'Advertisements',
          pageName: 'Advertisements',
          icon: faIcons.image,
          route: '/admin/ads',
          isLeafNode: true
        },
        {
          label: 'Product Management',
          pageName: 'Product Launch Management',
          icon: faIcons.box,
          route: '/admin/new-product-launch',
          isLeafNode: true
        },
        {
          label: 'Subscriptions',
          pageName: 'Subscription Dashboard',
          icon: faIcons.subscription,
          route: '/admin/subscription',
          isLeafNode: true
        },
        {
          label: 'Category Relationships',
          pageName: 'Category Relationship',
          icon: faIcons.gift,
          route: '/admin/category-relationship',
          isLeafNode: true
        },
        {
          label: 'System Settings',
          pageName: 'System Settings',
          icon: faIcons.settings,
          route: '/admin/system-settings',
          isLeafNode: true
        }
      ]
    },

    // ─── VENDOR ────────────────────────────────────────────────────────────
    {
      label: 'Vendor',
      role: 'vendor',
      icon: faIcons.box,
      route: '/vendor',
      isLeafNode: false,
      expanded: false,
      children: [
        {
          label: 'Home',
          pageName: 'Home',
          icon: faIcons.home,
          route: '/vendor/home',
          isLeafNode: true
        },
        {
          label: 'Dashboard',
          pageName: 'Dashboard',
          icon: faIcons.dashboard,
          route: '/vendor/dashboard',
          isLeafNode: true
        },
        {
          label: 'Checkout',
          pageName: 'Checkout',
          icon: faIcons.cart,
          route: '/vendor/qr-scanner',
          isLeafNode: true
        },
        {
          label:'Notifications',
          pageName:'Notifications',
          icon: faIcons.star,
          route:'/vendor/notifications',
          isLeafNode:true
        },

        {
          label: 'Sales',
          pageName: 'Sales (Loyalty)',
          icon: faIcons.chartLine,
          route: '/vendor/loyalty',
          isLeafNode: true
        },
        {
          label: 'Categories',
          pageName: 'Vendor Category',
          icon: faIcons.tags,
          route: '/vendor/category',
          isLeafNode: true
        },
        {
          label: 'Offers',
          pageName: 'Offers',
          icon: faIcons.gift,
          route: '/vendor/offers',
          isLeafNode: true
        },
        {
          label: 'New Product',
          pageName: 'New Product',
          icon: faIcons.box,
          route: '/vendor/newProduct',
          isLeafNode: true
        },
        {
          label: 'Reviews',
          pageName: 'Review Hub',
          icon: faIcons.star,
          route: '/vendor/review',
          isLeafNode: true
        },
        {
          label: 'Requests',
          pageName: 'Requests',
          icon: faIcons.clipboard,
          route: '/vendor/request',
          isLeafNode: true
        },
        {
          label: 'Advertisements',
          pageName: 'Advertisements',
          icon: faIcons.image,
          route: '/vendor/ads',
          isLeafNode: true
        },
        {
          label: 'Profile',
          pageName: 'Vendor Profile',
          icon: faIcons.userCircle,
          route: '/vendor/profile',
          isLeafNode: true
        },
        {
          label: 'Subscription',
          pageName: 'Subscription',
          icon: faIcons.creditCard,
          route: '/vendor/subscription',
          isLeafNode: true
        },
        // {
        //   label: 'Vendor Admin',
        //   pageName: 'Vendor Admin',
        //   icon: faIcons.shield,
        //   route: '/vendor/admin',
        //   isLeafNode: true
        // }
        // {
        //   label: 'Upload Checkout',
        //   pageName: 'Checkout Upload',
        //   icon: faIcons.upload,
        //   route: '/vendor/checkout-upload',
        //   isLeafNode: true
        // },
        // {
        //   label: 'Customer Reviews',
        //   pageName: 'Customer Reviews',
        //   icon: faIcons.tags,
        //   route: '/vendor/customerReviews',
        //   isLeafNode: true
        // },
      ]
    },

    {
      label: 'System Maintenance',
      pageName: 'System Settings',
      role: 'admin',
      icon: faIcons.shield,
      route: '/maintenance',
      isLeafNode: true
    },

    {
      label: 'Login',
      icon: faIcons.userCircle,
      isLeafNode: true
    }
  ];

  constructor(
    private authService: AuthService,
    private router: Router,
    private alert: AlertService,
    private impressionTracking: ImpressionTrackingService
  ) {}

  ngOnInit(): void {
    // Build on load (handles page-refresh with existing token)
    this.buildFilteredMenu();

    // ✅ Re-build whenever auth state changes (after login, logout, OR a
    // role switch — switchRole() re-applies storeSession() which fires
    // authState$, so this one subscription covers all three cases).
    this.authSub = this.authService.authState$.subscribe(() => {
      this.buildFilteredMenu();
    });
  }

  ngOnDestroy(): void {
    this.authSub?.unsubscribe();
  }

  buildFilteredMenu(): void {
    this.filteredMenuItems = this.filterMenu(this.menuItems);
    this.canSwitchRole = this.authService.canSwitchRole();
  }

  isLoggedIn(): boolean {
    return this.authService.isLoggedIn();
  }

  onLeafClick(item: MenuItem): void {
    if (item.label !== 'Login') return;

    if (this.isLoggedIn()) {
      const sessionId = localStorage.getItem('session_id');
      this.impressionTracking.reset();
      if (sessionId) {
        this.authService.logout(sessionId).subscribe({
          next: (res) => {
            this.alert.show(res.message, 'success');
            this.buildFilteredMenu();
            this.router.navigate(['/login']);
          },
          error: () => {
            this.alert.show('Logout Failed', 'error');
          }
        });
      } else {
        this.authService.removeToken();
        this.buildFilteredMenu();
        this.router.navigate(['/login']);
      }
    } else {
      this.router.navigate(['/login']);
    }
  }

  toggleMenu(item: MenuItem): void {
    if (!item.isLeafNode) {
      item.expanded = !item.expanded;
    }
  }

  // filterMenu(items: MenuItem[]): MenuItem[] {
  //   const filtered: MenuItem[] = [];

  //   for (const item of items) {
  //     // Non-leaf parent: recurse; show only if ≥1 child survives
  //     if (!item.isLeafNode && item.children?.length) {
  //       const visibleChildren = this.filterMenu(item.children);
  //       if (visibleChildren.length > 0) {
  //         filtered.push({ ...item, children: visibleChildren });
  //       }
  //       continue;
  //     }

  //     // Leaf with no pageName: always show (Login/Logout)
  //     if (!item.pageName) {
  //       filtered.push(item);
  //       continue;
  //     }

  //     // Leaf with pageName: show only if user's pages list contains it
  //     if (this.authService.hasAccess(item.pageName)) {
  //       filtered.push(item);
  //     }
  //   }

  //   return filtered;
  // }

  filterMenu(items: MenuItem[]): MenuItem[] {
    const role = this.authService.getUserRole();

    return items.filter(item => {

      // always show login/logout
      if (!item.pageName && !item.role) {
        return true;
      }

      // role mismatch -> hide entire branch
      if (item.role && item.role !== role) {
        return false;
      }

      // parent menu
      if (!item.isLeafNode && item.children?.length) {
        item.children = item.children.filter(child =>
          !child.pageName || this.authService.hasAccess(child.pageName)
        );

        return item.children.length > 0;
      }

      // leaf
      return !item.pageName || this.authService.hasAccess(item.pageName);
    });
  }

  toggleSidebar() {
    this.isSidebarOpen = !this.isSidebarOpen;
  }

  closeSidebar() {
    this.isSidebarOpen = false;
  }

  isMenuItemActive(item: MenuItem): boolean {
    if (!item.route) return false;

    const currentUrl = this.normalizeRoute(this.router.url);
    const itemRoute = this.normalizeRoute(item.route);

    if (currentUrl === itemRoute) return true;
    if (!currentUrl.startsWith(`${itemRoute}/`)) return false;

    return !this.hasMoreSpecificActiveRoute(itemRoute, currentUrl);
  }

  private hasMoreSpecificActiveRoute(itemRoute: string, currentUrl: string): boolean {
    return this.getLeafRoutes(this.filteredMenuItems).some(route => {
      const candidate = this.normalizeRoute(route);
      return candidate !== itemRoute
        && candidate.startsWith(`${itemRoute}/`)
        && (currentUrl === candidate || currentUrl.startsWith(`${candidate}/`));
    });
  }

  private getLeafRoutes(items: MenuItem[]): string[] {
    return items.flatMap(item => [
      ...(item.route && item.isLeafNode ? [item.route] : []),
      ...(item.children ? this.getLeafRoutes(item.children) : [])
    ]);
  }

  private normalizeRoute(route: string): string {
    const path = route.split('?')[0].split('#')[0];
    return path.length > 1 ? path.replace(/\/+$/, '') : path;
  }

  goToHome(): void {
    this.router.navigate(['/']);
  }
}